import { batch, Owner } from "solid-js";
import { reconcile } from "solid-js/store";

import Stripe from "@repo/ui/external/Stripe";
import { Delete, Get, Post } from "@repo/utils/api";
import Chainable from "@repo/utils/Chainable";
import UArray from "@repo/utils/UArray";
import UFunction from "@repo/utils/UFunction";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import PaymentMethod from "@repo/vygor-models/PaymentMethod";

import { api } from "..";

export { PaymentMethod };

export type PaymentMethodsRest = Get<
  {},
  { paymentMethods: PaymentMethod[] }
> & {
  [id: PaymentMethod.Id]: Get<{}, { paymentMethod: PaymentMethod }> &
    Delete<{}>;
  "stripe-cards": Post<
    { setupIntentId: string },
    { paymentMethod: PaymentMethod }
  > & {
    "setup-intent": Get<
      {},
      {
        setupIntentId: string;
        clientSecret: string;
      }
    >;
  };
};

const initialState = {
  byId: {} as Record<PaymentMethod.Id, PaymentMethod>,
  own: null as null | PaymentMethod.Id[],
  stripe: {
    clientSecret: null as null | string,
  },
};

export const { Context, Provider, usePaymentMethods } = FeatureStore.init(
  "PaymentMethods",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const accessors = {
      byId: (id: PaymentMethod.Id | undefined) => id && store.byId[id],
      own: attachAction(
        () => store.own?.map((id) => store.byId[id]!) || [],
        () => actions.fetch(),
        () => () => store.own,
      ),
      stripe: {
        clientSecret: attachAction(
          () => store.stripe.clientSecret,
          () => actions.stripe.fetchClientSecret(),
        ),
      },
    };

    const mutations = {
      one(paymentMethod: PaymentMethod) {
        setStore("byId", paymentMethod.id, reconcile(paymentMethod));
      },
      many(paymentMethods: PaymentMethod[]) {
        batch(() => {
          for (const paymentMethod of paymentMethods) {
            mutations.one(paymentMethod);
          }
        });
      },
      add(paymentMethod: PaymentMethod) {
        mutations.one(paymentMethod);
        setStore("own", (own) =>
          UArray.dedupe([...(own || []), paymentMethod.id]),
        );
      },
    };

    const actions = withLogger("action", {
      many: mutations.many,
      fetch() {
        return api["payment-methods"].GET({}).then(({ paymentMethods }) => {
          batch(() => {
            mutations.many(paymentMethods);
            setStore(
              "own",
              paymentMethods.map(({ id }) => id),
            );
          });
        });
      },
      remove(id: PaymentMethod.Id) {
        return api["payment-methods"][id]!.DELETE({}).then(() => {
          batch(() => {
            setStore("own", (paymentMethods) =>
              paymentMethods!.filter((paymentMethod) => paymentMethod != id),
            );
          });
        });
      },
      stripe: {
        fetchClientSecret() {
          return api["payment-methods"]["stripe-cards"]["setup-intent"]
            .GET({})
            .then(({ clientSecret }) => {
              setStore("stripe", { clientSecret });
            });
        },
        addPaymentMethod: Chainable.deferrable(
          ({ defer }) =>
            async (owner: Owner) => {
              try {
                const { setupIntent, error } =
                  await Stripe.PaymentElement.confirmSetup(owner);
                if (error) throw error;

                return await api["payment-methods"]["stripe-cards"]
                  .POST({
                    setupIntentId: setupIntent.id,
                  })
                  .then(async ({ paymentMethod }) => {
                    const resolve = UFunction.memo(() => {
                      mutations.add(paymentMethod);
                      return paymentMethod;
                    });
                    if (!defer) resolve();
                    return resolve;
                  });
              } catch (e) {
                console.error(e);
                throw e;
              }
            },
        ),
      },
    });

    return { accessors, actions };
  },
);
